import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';

export const LIFE_HACKS_PATH = '/guides?category=life';

const NAV_ITEMS = [
  { to: '/guides', label: 'Guides', paths: ['/guides'] },
  { to: LIFE_HACKS_PATH, label: 'Moving to Korea', paths: [] },
  { to: '/remittance', label: 'Money', paths: ['/remittance'] },
  { to: '/guides/f4-visa-guide', label: 'Visas', paths: [] },
  { to: '/about', label: 'About', paths: ['/about'] },
] as const;

export default function Header() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { theme, toggleTheme, language, toggleLanguage } = useUIStore();

  const handleLanguageToggle = () => {
    const newLanguage = language === 'en' ? 'ko' : 'en';
    i18n.changeLanguage(newLanguage);
    toggleLanguage();
  };

  const isActive = (item: (typeof NAV_ITEMS)[number]) => {
    if (item.to === LIFE_HACKS_PATH) {
      return location.pathname === '/guides' && location.search.includes('category=life');
    }
    if (item.to === '/guides/f4-visa-guide') {
      return location.pathname === item.to;
    }
    if (item.to === '/guides') {
      return location.pathname.startsWith('/guides') && location.pathname !== '/guides/f4-visa-guide';
    }
    return item.paths.some((path) => location.pathname.startsWith(path));
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 flex h-16 items-center border-b border-ink-200 bg-white px-4 dark:border-surface-dark-border dark:bg-surface-dark sm:px-6">
      <Link to="/" className="flex shrink-0 items-center gap-3" aria-label="KoreanHub home">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-jeok-500 to-[#A00D25] shadow-sm">
          <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
        </div>
        <span className="hidden text-xl font-bold text-ink-900 dark:text-ink-50 sm:block">
          KoreanHub<span className="text-ink-400">.net</span>
        </span>
      </Link>

      <nav className="ml-6 hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive(item)
                ? 'bg-jeok-500/5 text-jeok-500'
                : 'text-ink-500 hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-white/5 dark:hover:text-ink-50'
            }`}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <button
          type="button"
          onClick={handleLanguageToggle}
          className="rounded-lg px-2 py-2 text-sm font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-white/5 dark:hover:text-ink-50"
          aria-label="Toggle interface language between English and Korean"
          title="Interface language"
        >
          <span className={language === 'en' ? 'text-jeok-500' : undefined}>EN</span>
          <span className="mx-1 text-ink-300 dark:text-ink-500">/</span>
          <span className={language === 'ko' ? 'text-jeok-500' : undefined}>KR</span>
        </button>

        <button
          type="button"
          onClick={toggleTheme}
          className="rounded-lg p-2.5 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-white/5 dark:hover:text-ink-50"
          aria-label={theme === 'dark' ? 'Use light theme' : 'Use dark theme'}
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
      </div>
    </header>
  );
}
